import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Users,
    UserCheck,
    Clock4,
    AlertTriangle,
    AlertCircle,
    CheckCircle,
    Gift,
    Cake,
    PartyPopper,
    Filter,
    RefreshCw,
    Search,
    ChevronRight,
    Calendar as CalendarIcon,
    FileWarning,
    Inbox,
    Wallet,
    Plane,
    Receipt,
    PieChart,
    BarChart3,
    TrendingUp,
    Activity,
    Bell,
    CalendarDays,
} from "lucide-react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";

import {
    ADMIN_DASHBOARD,
    ADMIN_DASHBOARD_PRESENCE_LIST,
    EMPLOYEE_DEPARTMENT,
    HOME,
} from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard, EmployeeDetails } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import AssetDashboardSection from "./AssetDashboardSection";
import { DialogModal } from "../../components/ui/dialogModal";
import { AttendanceCard, TimesheetManagement } from "./Home";
import AttendanceEdit from "../attendance/AttendanceEdit";
import DayEndApprovalCard from "./DayEndApprovalCard";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

/* ----------------------------- shared helpers ----------------------------- */

const formatTime = (value) => {
    if (!value) return "--";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "--";
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
};

const formatDate = (value) => {
    if (!value) return "--";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "--";
    return d.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
};

const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return "0m";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h && m) return `${h}h ${m}m`;
    if (h) return `${h}h`;
    return `${m}m`;
};

const todayIso = () => new Date().toISOString().split("T")[0];

const isDarkMode = () =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark");

/* ------------------------------ ui primitives ----------------------------- */

const SectionCard = ({ icon: Icon, title, action, children, className = "", iconColor = "text-sidebar-primary" }) => (
    <div
        className={`bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 dark:border-gray-700 p-5 ${className}`}
    >
        <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-200 flex items-center gap-2">
                {Icon && <Icon className={`w-4 h-4 ${iconColor}`} />}
                {title}
            </h2>
            {action}
        </div>
        {children}
    </div>
);

const StatPill = ({ label, value, color = "blue", onClick }) => {
    const palette = {
        blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800",
        green: "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800",
        red: "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800",
        amber: "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
        violet: "bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800",
        slate: "bg-slate-50 dark:bg-slate-900/30 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700",
    };
    const interactive = typeof onClick === "function";
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={!interactive}
            className={`px-3 py-2 rounded-lg border ${palette[color]} flex items-center justify-between gap-3 min-w-0 text-left w-full ${interactive ? "hover:shadow-md hover:-translate-y-0.5 transition cursor-pointer" : "cursor-default"
                }`}
        >
            <span className="text-xs font-medium truncate">{label}</span>
            <span className="text-sm font-bold">{value}</span>
        </button>
    );
};

const EmptyState = ({ icon: Icon, label }) => (
    <div className="flex flex-col items-center justify-center py-8 text-gray-400 dark:text-gray-500">
        <Icon className="w-10 h-10 mb-2 opacity-50" />
        <p className="text-sm">{label}</p>
    </div>
);

const Skeleton = ({ className = "" }) => (
    <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded ${className}`} />
);

/* --------------------------------- filters -------------------------------- */

const DashboardFilters = ({ date, setDate, department, setDepartment, departments, onRefresh, refreshing }) => {
    const navigate = useNavigate();
    return (
        <div className="flex flex-wrap items-center gap-2">
            <DatePicker
                value={date ? dayjs(date) : null}
                onChange={(d) => setDate(d ? d.format("YYYY-MM-DD") : "")}
                format="DD-MM-YYYY"
                allowClear={false}
                size="large"
                formatter="DD-MM-YYYY"
                inputReadOnly
                disabledDate={(current) => current && current > dayjs().endOf("day")}
                className="min-w-[160px]"
                suffixIcon={<CalendarIcon className="w-4 h-4 text-gray-500" />}
            />
            <Select
                value={department || undefined}
                onChange={(value) => setDepartment(value || "")}
                placeholder="All Departments"
                size="large"
                allowClear
                className="min-w-[180px]"
                suffixIcon={<Filter className="w-4 h-4 text-gray-500" />}
                options={departments.map((d) => ({
                    value: d.id,
                    label: d.department_name || d.name,
                }))}
            />
            <Button
                onClick={() => navigate("/calendar")}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
                <CalendarDays className="w-4 h-4" />
                Your Calendar
            </Button>
            <Button variant="outline" size="default" onClick={onRefresh} disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                Refresh
            </Button>
        </div>
    )
};

/* ---------------------------- module: presence ---------------------------- */

const PresenceChart = ({ presence, loading, onSelect }) => {
    const dark = isDarkMode();
    const labels = ["Present", "Absent", "On Leave", "WFH", "Half-Day"];
    const values = useMemo(() => ([
        presence?.present || 0,
        presence?.absent || 0,
        presence?.on_leave || 0,
        // presence?.late || 0,
        presence?.work_from_home || 0,
        presence?.half_day || 0,
    ]), [presence]);

    const palette = ["#10b981", "#ef4444", "#f59e0b", "#f97316", "#3b82f6", "#8b5cf6"];

    const data = {
        labels,
        datasets: [
            {
                label: "Employees",
                data: values,
                backgroundColor: palette.map((c) => `${c}cc`),
                hoverBackgroundColor: palette,
                borderRadius: 8,
                borderSkipped: false,
                maxBarThickness: 38,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: dark ? "#1f2937" : "#111827",
                titleColor: "#fff",
                bodyColor: "#fff",
                padding: 10,
                cornerRadius: 8,
                callbacks: {
                    label: (ctx) => ` ${ctx.parsed.y} employees`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: dark ? "#9ca3af" : "#4b5563", font: { size: 11 } },
            },
            y: {
                beginAtZero: true,
                ticks: { color: dark ? "#9ca3af" : "#4b5563", precision: 0 },
                grid: { color: dark ? "rgba(75,85,99,0.2)" : "rgba(229,231,235,0.8)" },
            },
        },
    };

    return (
        <SectionCard
            icon={BarChart3}
            title="Today's Presence Analytics"
            iconColor="text-emerald-500"
            action={
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    Total: {presence?.total || 0}
                </span>
            }
        >
            {loading ? (
                <Skeleton className="h-64 w-full" />
            ) : (
                <>
                    <div className="h-64">
                        <Bar data={data} options={options} />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
                        <StatPill label="Present" value={values[0]} color="green" onClick={() => onSelect?.("present", "Present Today")} />
                        <StatPill label="Absent" value={values[1]} color="red" onClick={() => onSelect?.("absent", "Absent Today")} />
                        <StatPill label="On Leave" value={values[2]} color="amber" onClick={() => onSelect?.("on_leave", "On Leave Today")} />
                        {/* <StatPill label="Late" value={values[3]} color="amber" onClick={() => onSelect?.("late", "Late Comers Today")} /> */}
                        <StatPill label="WFH" value={values[3]} color="blue" onClick={() => onSelect?.("wfh", "Working From Home Today")} />
                        <StatPill label="Half-Day" value={values[4]} color="violet" onClick={() => onSelect?.("half_day", "Half-Day Employees Today")} />
                    </div>
                </>
            )}
        </SectionCard>
    );
};

/* -------------------------- module: pending admin ------------------------- */

const PendingApprovalsChart = ({ approvals, loading }) => {
    const navigate = useNavigate();
    const dark = isDarkMode();
    const labels = ["Reimbursements", "Leaves", "Advances"];
    const values = useMemo(() => ([
        approvals?.reimbursement || 0,
        approvals?.leave || 0,
        approvals?.advance || 0,
    ]), [approvals]);

    const colors = ["#0ea5e9", "#6366f1", "#f59e0b"];
    const navTargets = [
        { path: "/employee/approvalinbox", state: { activeTab: "reimbursement" } },
        { path: "/employee/approvalinbox", state: { activeTab: "leave" } },
        { path: "/employee/approvalinbox", state: { activeTab: "advance" } },
    ];

    const data = {
        labels,
        datasets: [
            {
                label: "Pending",
                data: values,
                backgroundColor: colors.map((c) => `${c}cc`),
                hoverBackgroundColor: colors,
                borderRadius: 8,
                borderSkipped: false,
                maxBarThickness: 56,
            },
        ],
    };

    const options = {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        onClick: (_, elements) => {
            if (!elements.length) return;
            const target = navTargets[elements[0].index];
            navigate(target.path, { state: target.state });
        },
        onHover: (event, elements) => {
            const target = event?.native?.target;
            if (target) target.style.cursor = elements.length ? "pointer" : "default";
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: dark ? "#1f2937" : "#111827",
                titleColor: "#fff",
                bodyColor: "#fff",
                padding: 10,
                cornerRadius: 8,
                callbacks: { label: (ctx) => ` ${ctx.parsed.x} pending` },
            },
        },
        scales: {
            x: {
                beginAtZero: true,
                ticks: { color: dark ? "#9ca3af" : "#4b5563", precision: 0 },
                grid: { color: dark ? "rgba(75,85,99,0.2)" : "rgba(229,231,235,0.8)" },
            },
            y: {
                grid: { display: false },
                ticks: { color: dark ? "#9ca3af" : "#4b5563", font: { size: 12, weight: "500" } },
            },
        },
    };

    const total = values.reduce((a, b) => a + b, 0);

    return (
        <SectionCard
            icon={Inbox}
            iconColor="text-indigo-500"
            title="Pending Admin Actions"
            action={
                <button
                    onClick={() => navigate("/employee/approvalinbox")}
                    className="text-xs font-medium text-sidebar-primary hover:underline flex items-center gap-1"
                >
                    Open inbox <ChevronRight className="w-3 h-3" />
                </button>
            }
        >
            {loading ? (
                <Skeleton className="h-64 w-full" />
            ) : (
                <>
                    <div className="h-56">
                        <Bar data={data} options={options} />
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-2">
                        <button
                            onClick={() => navigate("/employee/approvalinbox", { state: { activeTab: "reimbursement" } })}
                            className="group text-left p-2 rounded-lg bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 hover:shadow-sm transition"
                        >
                            <div className="flex items-center gap-1 text-xs font-medium text-sky-700 dark:text-sky-300">
                                <Receipt className="w-3 h-3" /> Reimburse
                            </div>
                            <div className="text-lg font-bold text-sky-800 dark:text-sky-200">{values[0]}</div>
                        </button>
                        <button
                            onClick={() => navigate("/employee/approvalinbox", { state: { activeTab: "leave" } })}
                            className="group text-left p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 hover:shadow-sm transition"
                        >
                            <div className="flex items-center gap-1 text-xs font-medium text-indigo-700 dark:text-indigo-300">
                                <Plane className="w-3 h-3" /> Leave
                            </div>
                            <div className="text-lg font-bold text-indigo-800 dark:text-indigo-200">{values[1]}</div>
                        </button>
                        <button
                            onClick={() => navigate("/employee/approvalinbox", { state: { activeTab: "advance" } })}
                            className="group text-left p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 hover:shadow-sm transition"
                        >
                            <div className="flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-300">
                                <Wallet className="w-3 h-3" /> Advance
                            </div>
                            <div className="text-lg font-bold text-amber-800 dark:text-amber-200">{values[2]}</div>
                        </button>
                    </div>
                    {total > 10 && (
                        <div className="mt-3 flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                            <AlertTriangle className="w-3 h-3" />
                            High backlog — {total} pending items
                        </div>
                    )}
                </>
            )}
        </SectionCard>
    );
};

/* -------------------------- module: correction queue ---------------------- */

const CorrectionQueueDonut = ({ correction, loading, onOpenDrawer }) => {
    const dark = isDarkMode();
    const data = useMemo(() => ({
        labels: ["Pending", "Approved", "Rejected"],
        datasets: [
            {
                data: [
                    correction?.pending || 0,
                    correction?.approved || 0,
                    correction?.rejected || 0,
                ],
                backgroundColor: ["#f59e0b", "#10b981", "#ef4444"],
                borderColor: dark ? "#1f2937" : "#ffffff",
                borderWidth: 3,
                hoverOffset: 8,
            },
        ],
    }), [correction, dark]);

    const total = (correction?.pending || 0) + (correction?.approved || 0) + (correction?.rejected || 0);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "70%",
        plugins: {
            legend: {
                position: "bottom",
                labels: { color: dark ? "#d1d5db" : "#374151", boxWidth: 10, padding: 12, font: { size: 11 } },
            },
            tooltip: {
                backgroundColor: dark ? "#1f2937" : "#111827",
                titleColor: "#fff",
                bodyColor: "#fff",
                padding: 10,
                cornerRadius: 8,
            },
        },
    };

    return (
        <SectionCard
            icon={PieChart}
            iconColor="text-amber-500"
            title="Correction Queue"
            action={
                <button
                    onClick={onOpenDrawer}
                    className="text-xs font-medium text-sidebar-primary hover:underline flex items-center gap-1"
                >
                    Open drawer <ChevronRight className="w-3 h-3" />
                </button>
            }
        >
            {loading ? (
                <Skeleton className="h-64 w-full" />
            ) : total === 0 ? (
                <EmptyState icon={PieChart} label="No correction requests yet" />
            ) : (
                <div className="relative">
                    <div className="h-64">
                        <Doughnut data={data} options={options} />
                    </div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none -mt-8">
                        <div className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                            {correction?.pending || 0}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Pending
                        </div>
                    </div>
                </div>
            )}
        </SectionCard>
    );
};

/* --------------------------- module: late comers -------------------------- */

const LateComers = ({ items, loading }) => {
    const [search, setSearch] = useState("");
    const navigate = useNavigate();

    const filtered = useMemo(() => {
        if (!search) return items;
        const q = search.toLowerCase();
        return items.filter((i) => {
            const name = i.employee_details?.employee_name?.toLowerCase() || "";
            const dept = i.employee_details?.department?.name?.toLowerCase() || "";
            return name.includes(q) || dept.includes(q);
        });
    }, [items, search]);

    return (
        <SectionCard
            icon={Clock4}
            iconColor="text-orange-500"
            title="Late Comers (Today)"
            action={
                <span className="text-xs text-gray-500 dark:text-gray-400">
                    {items.length} late
                </span>
            }
        >
            <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name or department..."
                    className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg outline-none focus:border-sidebar-primary text-gray-800 dark:text-gray-100 placeholder-gray-400"
                />
            </div>
            <div className="space-y-2 max-h-72 overflow-auto pr-1">
                {loading ? (
                    Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)
                ) : filtered.length === 0 ? (
                    <EmptyState icon={UserCheck} label="No late comers today" />
                ) : (
                    filtered.map((item) => {
                        const severe = item.delay_minutes >= 30;
                        return (
                            <div
                                key={item.attendance_id}
                                onClick={() =>
                                    navigate("/attendance/dayattendance", {
                                        state: { employeeId: item.employee_details?.id },
                                    })
                                }
                                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition hover:shadow-md ${severe
                                    ? "border-red-200 dark:border-red-800 bg-red-50/40 dark:bg-red-900/10"
                                    : "border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-900/40 hover:bg-white dark:hover:bg-gray-800"
                                    }`}
                            >
                                <EmployeeDetails
                                    details={{
                                        title: item.employee_details?.title,
                                        first_name: item.employee_details?.employee_name,
                                        last_name: "",
                                        designation_id: {
                                            designation_name: item.employee_details?.designation?.name,
                                        },
                                        id: item.employee_details?.employee_id,
                                        profile: item.employee_details?.profile,
                                    }}
                                    size="sm"
                                />
                                <div className="text-right shrink-0">
                                    <div className="flex items-center gap-1 justify-end">
                                        <span
                                            className={`text-xs font-semibold px-2 py-0.5 rounded-full ${severe
                                                ? "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"
                                                : "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300"
                                                }`}
                                        >
                                            {formatDuration(item.delay_minutes)}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {item.shift_start_time} • {formatTime(item.check_in)}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </SectionCard>
    );
};

/* ------------------------ module: incomplete logs ------------------------- */

const IncompleteLogs = ({ items, loading, onEdit }) => {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");

    const filtered = useMemo(() => {
        if (!search) return items;
        const q = search.toLowerCase();
        return items.filter((i) => {
            const name = i.employee_details?.employee_name?.toLowerCase() || "";
            const missing = i.missing?.toLowerCase() || "";
            return name.includes(q) || missing.includes(q);
        });
    }, [items, search]);

    const exportCsv = () => {
        const header = "Employee,Department,Date,Shift,Missing,Status\n";
        const rows = items.map((i) => {
            const emp = i.employee_details || {};
            return [
                emp.employee_name || "",
                emp.department?.name || "",
                i.date || "",
                i.shift || "",
                i.missing || "",
                i.status || "",
            ]
                .map((v) => `"${String(v).replace(/"/g, '""')}"`)
                .join(",");
        });
        const blob = new Blob([header + rows.join("\n")], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `incomplete-logs-${todayIso()}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <SectionCard
            icon={FileWarning}
            iconColor="text-rose-500"
            title="Incomplete Logs"
            action={
                <button
                    onClick={exportCsv}
                    className="text-xs font-medium text-sidebar-primary hover:underline"
                >
                    Export CSV
                </button>
            }
        >
            <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Filter logs..."
                    className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg outline-none focus:border-sidebar-primary text-gray-800 dark:text-gray-100 placeholder-gray-400"
                />
            </div>
            <div className="overflow-auto max-h-72">
                {loading ? (
                    <div className="space-y-2">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} className="h-12 w-full" />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <EmptyState icon={CheckCircle} label="All attendance logs complete" />
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                            <tr>
                                <th className="text-left py-2 px-2 font-medium">Employee</th>
                                <th className="text-left py-2 px-2 font-medium hidden md:table-cell">Date</th>
                                <th className="text-left py-2 px-2 font-medium hidden lg:table-cell">Shift</th>
                                <th className="text-left py-2 px-2 font-medium">Missing</th>
                                <th className="text-right py-2 px-2 font-medium">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {filtered.map((row) => (
                                <tr
                                    key={row.attendance_id}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-900/40 transition"
                                >
                                    <td className="py-2 px-2">
                                        <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
                                            {row.employee_details?.employee_name || "-"}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {row.employee_details?.department?.name || ""}
                                        </div>
                                    </td>
                                    <td className="py-2 px-2 text-gray-700 dark:text-gray-300 hidden md:table-cell text-xs">
                                        {formatDate(row.date)}
                                    </td>
                                    <td className="py-2 px-2 text-gray-700 dark:text-gray-300 hidden lg:table-cell text-xs">
                                        {row.shift || "-"}
                                    </td>
                                    <td className="py-2 px-2">
                                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300">
                                            {row.missing}
                                        </span>
                                    </td>
                                    <td className="py-2 px-2 text-right">
                                        <button
                                            onClick={() => onEdit?.(row)}
                                            className="text-xs font-medium text-sidebar-primary hover:underline"
                                        >
                                            Correct
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </SectionCard>
    );
};

/* ----------------------- module: announcements ---------------------------- */

const priorityChip = {
    high: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    low: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
};

const Announcements = ({ items, loading }) => {
    const navigate = useNavigate();
    const list = items || [];

    return (
        <SectionCard
            icon={Bell}
            iconColor="text-yellow-500"
            title="Announcements"
            action={
                <button
                    onClick={() => navigate("/admin/noticeboard")}
                    className="text-xs font-medium text-sidebar-primary hover:underline flex items-center gap-1"
                >
                    Manage <ChevronRight className="w-3 h-3" />
                </button>
            }
        >
            {loading ? (
                <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} className="h-14 w-full" />
                    ))}
                </div>
            ) : list.length === 0 ? (
                <EmptyState icon={Bell} label="No active announcements" />
            ) : (
                <div className="max-h-72 overflow-auto space-y-2 pr-1">
                    {list.map((n) => {
                        const priority = (n.priority || "").toLowerCase();
                        const chip = priorityChip[priority] || "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
                        return (
                            <div
                                key={n.id}
                                className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-900/40 hover:bg-white dark:hover:bg-gray-800 transition"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                                            {n.title || "Notice"}
                                        </p>
                                        {n.description && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                                                {n.description}
                                            </p>
                                        )}
                                    </div>
                                    {priority && (
                                        <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${chip}`}>
                                            {priority.toUpperCase()}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1.5 flex items-center justify-between text-[11px] text-gray-400 dark:text-gray-500">
                                    <span>{n.notice_by || "HR Team"}</span>
                                    <span>{formatDate(n.published_date)}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </SectionCard>
    );
};

/* ------------------- module: critical approvals queue --------------------- */

const CriticalApprovalsQueue = ({ approvals, correction, loading }) => {
    const navigate = useNavigate();
    const total = (approvals?.total || 0) + (correction?.pending || 0);

    const cards = [
        {
            key: "leave",
            icon: Plane,
            label: "Leave Requests",
            count: approvals?.leave || 0,
            color: "indigo",
            target: "/employee/approvalinbox",
            tab: "leave",
        },
        {
            key: "reimburse",
            icon: Receipt,
            label: "Reimbursements",
            count: approvals?.reimbursement || 0,
            color: "sky",
            target: "/employee/approvalinbox",
            tab: "reimbursement",
        },
        {
            key: "advance",
            icon: Wallet,
            label: "Advances",
            count: approvals?.advance || 0,
            color: "amber",
            target: "/employee/approvalinbox",
            tab: "advance",
        },
    ];

    const colorClass = {
        indigo: "from-indigo-500 to-indigo-600",
        sky: "from-sky-500 to-sky-600",
        amber: "from-amber-500 to-amber-600",
    };

    return (
        <SectionCard
            icon={Activity}
            iconColor="text-rose-500"
            title="Critical Approvals Queue"
            action={
                <div className="flex items-center gap-2">
                    {total > 0 && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 animate-pulse">
                            {total} URGENT
                        </span>
                    )}
                </div>
            }
        >
            {loading ? (
                <div className="grid gap-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} className="h-20 w-full" />
                    ))}
                </div>
            ) : total === 0 ? (
                <EmptyState icon={CheckCircle} label="You're all caught up 🎉" />
            ) : (
                <div className="grid grid-cols-1 gap-3">
                    {cards
                        .filter((c) => c.count > 0)
                        .sort((a, b) => b.count - a.count)
                        .map((c) => (
                            <button
                                key={c.key}
                                onClick={() => navigate(c.target, { state: { activeTab: c.tab } })}
                                className="group relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40 hover:shadow-md transition text-left p-3"
                            >
                                <div
                                    className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${colorClass[c.color]}`}
                                />
                                <div className="flex items-center justify-between pl-2">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClass[c.color]} flex items-center justify-center text-white shadow-sm`}
                                        >
                                            <c.icon className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <div className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                                {c.label}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-2">
                                                <span className="inline-flex items-center gap-1">
                                                    <AlertTriangle className="w-3 h-3" />
                                                    High priority
                                                </span>
                                                {c.count > 5 && (
                                                    <span className="text-rose-600 dark:text-rose-400 font-semibold">
                                                        SLA risk
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                                            {c.count}
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </div>
                            </button>
                        ))}
                </div>
            )}
        </SectionCard>
    );
};

/* ------------------------ module: weekly celebrations --------------------- */

const Avatar = ({ src, name }) => {
    const initial = (name || "?").charAt(0).toUpperCase();
    return src ? (
        <img
            src={src}
            alt={name || ""}
            className="w-10 h-10 rounded-full object-cover border-2 border-white dark:border-gray-700 shadow-sm"
        />
    ) : (
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 text-white flex items-center justify-center text-sm font-semibold border-2 border-white dark:border-gray-700 shadow-sm">
            {initial}
        </div>
    );
};

const BirthdayCard = ({ birthdays, loading }) => (
    <SectionCard
        icon={Cake}
        iconColor="text-pink-500"
        title="Birthdays This Week"
        action={
            <span className="text-xs text-gray-500 dark:text-gray-400">{birthdays.length} celebrations</span>
        }
    >
        <div className="max-h-72 overflow-auto space-y-2 pr-1">
            {loading ? (
                Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)
            ) : birthdays.length === 0 ? (
                <EmptyState icon={Cake} label="No birthdays this week" />
            ) : (
                birthdays.map((b) => (
                    <div
                        key={b.employee_id}
                        className="flex items-center gap-3 p-2.5 rounded-lg bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-900/10 dark:to-rose-900/10 border border-pink-100 dark:border-pink-900/30 hover:shadow-sm transition"
                    >
                        <Avatar src={b.profile} name={b.employee_name} />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                                    {b.employee_name}
                                </p>
                                {b.is_today && (
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-pink-500 text-white animate-pulse">
                                        TODAY
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {b.department || b.designation || "—"}
                            </p>
                        </div>
                        <div className="text-right shrink-0">
                            <Gift className="w-4 h-4 text-pink-500 mb-0.5 ml-auto" />
                            <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(b.date)}</p>
                        </div>
                    </div>
                ))
            )}
        </div>
    </SectionCard>
);

const AnniversaryCard = ({ anniversaries, loading }) => (
    <SectionCard
        icon={PartyPopper}
        iconColor="text-violet-500"
        title="Work Anniversaries This Week"
        action={
            <span className="text-xs text-gray-500 dark:text-gray-400">
                {anniversaries.length} celebrations
            </span>
        }
    >
        <div className="max-h-72 overflow-auto space-y-2 pr-1">
            {loading ? (
                Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)
            ) : anniversaries.length === 0 ? (
                <EmptyState icon={PartyPopper} label="No anniversaries this week" />
            ) : (
                anniversaries.map((a) => (
                    <div
                        key={a.employee_id}
                        className="flex items-center gap-3 p-2.5 rounded-lg bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/10 dark:to-indigo-900/10 border border-violet-100 dark:border-violet-900/30 hover:shadow-sm transition"
                    >
                        <Avatar src={a.profile} name={a.employee_name} />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                                    {a.employee_name}
                                </p>
                                {a.is_today && (
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-500 text-white animate-pulse">
                                        TODAY
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {a.department || a.designation || "—"}
                            </p>
                        </div>
                        <div className="text-right shrink-0">
                            <div className="text-sm font-bold text-violet-600 dark:text-violet-400">
                                {a.years_completed} yrs
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(a.date)}</p>
                        </div>
                    </div>
                ))
            )}
        </div>
    </SectionCard>
);

/* ----------------------- presence detail dialog --------------------------- */

const STATUS_BADGE = {
    present: { label: "Present", chip: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" },
    absent: { label: "Absent", chip: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" },
    on_leave: { label: "On Leave", chip: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
    late: { label: "Late", chip: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300" },
    wfh: { label: "WFH", chip: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
    half_day: { label: "Half-Day", chip: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" },
};

const PresenceDetailDialog = ({ open, onClose, statusKey, title, date, department }) => {
    const { fetch } = useApiMethods();
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (!open || !statusKey) return;
        setLoading(true);
        setSearch("");
        const params = { status: statusKey };
        if (date) params.date = date;
        if (department) params.department = department;
        fetch(ADMIN_DASHBOARD_PRESENCE_LIST, params)
            .then((res) => {
                if (res?.status === "success") {
                    setList(res.data?.results || []);
                } else {
                    setList([]);
                    ToastMsg("error", res?.message || "Failed to load list");
                }
            })
            .catch((err) => {
                setList([]);
                ToastMsg("error", err?.message || "Failed to load list");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, statusKey, date, department]);

    const filtered = useMemo(() => {
        if (!search) return list;
        const q = search.toLowerCase();
        return list.filter((item) => {
            const name = item.employee_details?.employee_name?.toLowerCase() || "";
            const dept = item.employee_details?.department?.name?.toLowerCase() || "";
            const desig = item.employee_details?.designation?.name?.toLowerCase() || "";
            return name.includes(q) || dept.includes(q) || desig.includes(q);
        });
    }, [list, search]);

    const badge = STATUS_BADGE[statusKey] || STATUS_BADGE.present;

    return (
        <div style={{ margin: 0 }}>
            <DialogModal
                open={open}
                onClose={onClose}
                title={title}
                showActions={false}
                width="640px"
            >
                <div className="flex items-center justify-between mb-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.chip}`}>
                        {badge.label}
                        <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/60 dark:bg-gray-900/40 text-[10px]">
                            {list.length}
                        </span>
                    </span>
                    <div className="relative w-1/2">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search employees..."
                            className="w-full pl-9 pr-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg outline-none focus:border-sidebar-primary text-gray-800 dark:text-gray-100 placeholder-gray-400"
                        />
                    </div>
                </div>
                <div className="max-h-[55vh] overflow-auto pr-1 space-y-2">
                    {loading ? (
                        Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)
                    ) : filtered.length === 0 ? (
                        <EmptyState icon={UserCheck} label="No employees in this category" />
                    ) : (
                        filtered.map((item, idx) => {
                            const emp = item.employee_details || {};
                            return (
                                <div
                                    key={`${emp.id || idx}-${idx}`}
                                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-900/40 hover:bg-white dark:hover:bg-gray-800 transition"
                                >
                                    <EmployeeDetails
                                        details={{
                                            title: emp.title,
                                            first_name: emp.employee_name,
                                            last_name: "",
                                            designation_id: {
                                                designation_name: emp.designation?.name,
                                            },
                                            id: emp.employee_id,
                                            profile: emp.profile,
                                        }}
                                        size="sm"
                                    />
                                    <div className="text-right text-xs text-gray-500 dark:text-gray-400 shrink-0 ml-3">
                                        {statusKey === "late" && item.delay_minutes != null && (
                                            <div className="font-semibold text-orange-600 dark:text-orange-400">
                                                {formatDuration(item.delay_minutes)} late
                                            </div>
                                        )}
                                        {statusKey === "present" && item.is_late && (
                                            <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 mb-0.5">
                                                LATE
                                            </span>
                                        )}
                                        {item.check_in && (
                                            <div>In: {formatTime(item.check_in)}</div>
                                        )}
                                        {item.check_out && (
                                            <div>Out: {formatTime(item.check_out)}</div>
                                        )}
                                        {item.leave_type && (
                                            <div className="text-amber-600 dark:text-amber-400">
                                                {item.leave_type}
                                            </div>
                                        )}
                                        {item.shift_start_time && !item.check_in && (
                                            <div>Shift: {item.shift_start_time}</div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </DialogModal>
        </div>
    );
};

/* ---------------------------- top header / greeting ----------------------- */

const DashboardHeader = ({ user, lastRefreshed, filters }) => {
    const greeting = useMemo(() => {
        const h = new Date().getHours();
        if (h < 12) return "Good Morning";
        if (h < 17) return "Good Afternoon";
        if (h < 21) return "Good Evening";
        return "Good Night";
    }, []);

    return (
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
            <div>
                <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                    {greeting}, {user}
                    <span className="hidden sm:inline text-xl">👋</span>
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Workforce overview • {new Date().toLocaleDateString("en-US", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                    })}
                    {lastRefreshed && (
                        <span className="text-xs text-gray-400 ml-2">
                            • updated {formatTime(lastRefreshed)}
                        </span>
                    )}
                </p>
            </div>
            <div>{filters}</div>
        </div>
    );
};

/* -------------------------------- the page -------------------------------- */

const AdminDashboard = () => {
    const { fetch } = useApiMethods();

    // Asset Requests overview is for asset managers only.
    const isAssetAdmin = JSON.parse(sessionStorage.getItem("is_system_admin") || "false")
        || JSON.parse(sessionStorage.getItem("is_super_admin") || "false");

    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [date, setDate] = useState(todayIso());
    const [department, setDepartment] = useState("");
    const [lastRefreshed, setLastRefreshed] = useState(null);
    const [correctionDrawerOpen, setCorrectionDrawerOpen] = useState(false);
    const [presenceDetail, setPresenceDetail] = useState({ open: false, status: null, title: "" });
    const [attendanceEditDialog, setAttendanceEditDialog] = useState({ open: false, data: null });
    const [attendanceEditSaving, setAttendanceEditSaving] = useState(false);
    const attendanceEditRef = useRef(null);
    const [attendanceData, setAttendanceData] = useState(() => {
        const stored = sessionStorage.getItem("shiftDetails");
        return stored ? JSON.parse(stored) : null;
    });
    const [attendanceLoading, setAttendanceLoading] = useState(false);

    const userName = sessionStorage.getItem("employee_name") || "Admin";
    const pollingRef = useRef(null);

    const loadDashboard = useCallback(
        async (silent = false) => {
            if (!silent) setLoading(true);
            setRefreshing(true);
            try {
                const params = {};
                if (date) params.date = date;
                if (department) params.department = department;
                const response = await fetch(ADMIN_DASHBOARD, params);
                if (response?.status === "success") {
                    setDashboard(response.data);
                    setLastRefreshed(new Date());

                    // Mirror Home.js — persist customConfig + password policy so
                    // gates like TrainingGuard (is_nabh) keep working when an
                    // admin lands here without first visiting /home.
                    let hydrated = false;
                    const customConfig = response.data?.payroll_custom_configuration?.[0];
                    if (customConfig) {
                        sessionStorage.setItem("customConfig", JSON.stringify(customConfig));
                        hydrated = true;
                    }
                    const passwordPolicy = response.data?.password_policy?.[0];
                    if (passwordPolicy) {
                        sessionStorage.setItem("password_policy", JSON.stringify(passwordPolicy));
                        hydrated = true;
                    }
                    if (response.data?.salary_calculation) {
                        sessionStorage.setItem("salary_calculation", response.data.salary_calculation);
                        hydrated = true;
                    }
                    if (hydrated) {
                        // Tell the sidebar / nav hook to re-read sessionStorage so
                        // flag-gated items (e.g. Training) appear without a refresh.
                        window.dispatchEvent(new Event("hrms:session-storage-hydrated"));
                    }
                } else {
                    ToastMsg("error", response?.message || "Failed to load dashboard");
                }
            } catch (err) {
                console.error("admin dashboard error", err);
                ToastMsg("error", err?.message || "Something went wrong loading the dashboard");
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [date, department]
    );

    const loadDepartments = useCallback(async () => {
        try {
            const response = await fetch(EMPLOYEE_DEPARTMENT, { drop_down: "True" });
            const list = response?.data?.result || response?.data || [];
            setDepartments(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error("department fetch error", err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshAttendance = useCallback(async () => {
        setAttendanceLoading(true);
        try {
            const response = await fetch(HOME);
            if (response?.status === "success" && response.data) {
                const shiftDetails = {
                    attendance_id: response.data.attendance_id,
                    date: response.data.date,
                    check_in_time: response.data.check_in,
                    check_out_time: response.data.check_out,
                    shift_end_time: response.data.shift_end_time,
                    shift_start_time: response.data.shift_start_time,
                    working_hrs: response.data.working_hrs,
                    clock_status: response.data.clock_status,
                    pause: response.data.pause,
                    resume: response.data.resume,
                };
                sessionStorage.setItem("shiftDetails", JSON.stringify(shiftDetails));
                setAttendanceData(shiftDetails);
            }
        } catch (err) {
            console.error("attendance refresh error", err);
        } finally {
            setAttendanceLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, department]);

    useEffect(() => {
        loadDepartments();
        if (!attendanceData) refreshAttendance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        pollingRef.current = setInterval(() => loadDashboard(true), 60000);
        return () => clearInterval(pollingRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, department]);

    const presence = dashboard?.presence;
    const approvals = dashboard?.pending_approvals;
    const correction = dashboard?.correction_queue;
    const lateComers = dashboard?.late_comers || [];
    const incompleteLogs = dashboard?.incomplete_logs || [];
    const birthdays = dashboard?.celebrations?.birthdays || [];
    const anniversaries = dashboard?.celebrations?.anniversaries || [];
    const announcements = dashboard?.notice_board || [];

    return (
        <ContainerCard>
            <div className="p-2 space-y-5">
                <DashboardHeader
                    user={userName}
                    lastRefreshed={lastRefreshed}
                    filters={
                        <DashboardFilters
                            date={date}
                            setDate={setDate}
                            department={department}
                            setDepartment={setDepartment}
                            departments={departments}
                            onRefresh={() => loadDashboard()}
                            refreshing={refreshing}
                        />
                    }
                />

                {/* TOP: clockify + quick stats */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                    <div className="lg:col-span-1">
                        <AttendanceCard
                            fromDashboard={true}
                            attendanceData={attendanceData}
                            attendanceLoading={attendanceLoading}
                            refreshData={refreshAttendance}
                        />
                    </div>
                    <div className="lg:col-span-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <QuickStat
                            icon={Users}
                            label="Total Workforce"
                            value={presence?.total ?? "—"}
                            color="blue"
                            loading={loading}
                        />
                        <QuickStat
                            icon={UserCheck}
                            label="Present Today"
                            value={presence?.present ?? "—"}
                            color="green"
                            loading={loading}
                        />
                        <QuickStat
                            icon={Inbox}
                            label="Pending Approvals"
                            value={approvals?.total ?? "—"}
                            color="amber"
                            loading={loading}
                        />
                        {/* <QuickStat
                            icon={AlertCircle}
                            label="Correction Pending"
                            value={correction?.pending ?? "—"}
                            color="rose"
                            loading={loading}
                            onClick={() => setCorrectionDrawerOpen(true)}
                        /> */}
                    </div>
                </div>

                {/* ROW 1: presence + pending */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <PresenceChart
                        presence={presence}
                        loading={loading}
                        onSelect={(statusKey, title) =>
                            setPresenceDetail({ open: true, status: statusKey, title })
                        }
                    />
                    <PendingApprovalsChart approvals={approvals} loading={loading} />
                </div>

                {/* ROW: Day-end report approvals (team timesheet) */}
                <DayEndApprovalCard />

                {/* ROW 2: correction queue + late comers */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <CorrectionQueueDonut
                        correction={correction}
                        loading={loading}
                        onOpenDrawer={() => setCorrectionDrawerOpen(true)}
                    />
                    <LateComers items={lateComers} loading={loading} />
                </div>

                {/* ROW 3: incomplete logs + critical approvals */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <IncompleteLogs
                        items={incompleteLogs}
                        loading={loading}
                        onEdit={(row) => setAttendanceEditDialog({ open: true, data: row })}
                    />
                    <Announcements items={announcements} loading={loading} />
                </div>

                {/* ROW 4: celebrations */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <BirthdayCard birthdays={birthdays} loading={loading} />
                    <AnniversaryCard anniversaries={anniversaries} loading={loading} />
                </div>

                {/* ROW 5: Asset Requests overview — system admins only */}
                {isAssetAdmin && (
                    <div className="space-y-3">
                        <AssetDashboardSection />
                    </div>
                )}

                {/* Reuse TimesheetManagement drawer for correction queue */}
                <TimesheetManagement
                    hideTrigger
                    openExternal={correctionDrawerOpen}
                    onOpenChange={setCorrectionDrawerOpen}
                    defaultTab="requests"
                />

                <PresenceDetailDialog
                    open={presenceDetail.open}
                    onClose={() => setPresenceDetail((p) => ({ ...p, open: false }))}
                    statusKey={presenceDetail.status}
                    title={presenceDetail.title}
                    date={date}
                    department={department}
                />

                <div style={{ margin: 0 }}>
                    <DialogModal
                        open={attendanceEditDialog.open}
                        onClose={() => {
                            setAttendanceEditDialog({ open: false, data: null });
                        }}
                        onSave={async () => {
                            try {
                                setAttendanceEditSaving(true);
                                if (attendanceEditRef.current) {
                                    await attendanceEditRef.current.handleSubmit();
                                }
                                setAttendanceEditDialog({ open: false, data: null });
                                loadDashboard();
                            } catch (err) {
                                console.error("Attendance edit failed", err);
                            } finally {
                                setAttendanceEditSaving(false);
                            }
                        }}
                        saveLabel="Save"
                        loading={attendanceEditSaving}
                        title="Edit Attendance"
                        width="900px"
                    >
                        {attendanceEditDialog.data && (
                            <AttendanceEdit
                                ref={attendanceEditRef}
                                editDetails={attendanceEditDialog.data}
                                onSuccess={() => {
                                    setAttendanceEditDialog({ open: false, data: null });
                                    loadDashboard();
                                }}
                            />
                        )}
                    </DialogModal>
                </div>
            </div>
        </ContainerCard>
    );
};

/* ------------------------------- quick stat ------------------------------- */

const QuickStat = ({ icon: Icon, label, value, color = "blue", loading, onClick }) => {
    const palette = {
        blue: {
            bg: "from-blue-500 to-blue-600",
            chip: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300",
        },
        green: {
            bg: "from-emerald-500 to-emerald-600",
            chip: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300",
        },
        amber: {
            bg: "from-amber-500 to-amber-600",
            chip: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
        },
        rose: {
            bg: "from-rose-500 to-rose-600",
            chip: "bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300",
        },
    };

    return (
        <button
            type="button"
            disabled={!onClick}
            onClick={onClick}
            className={`relative overflow-hidden rounded-2xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-left transition ${onClick ? "hover:shadow-md cursor-pointer" : "cursor-default"
                }`}
        >
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {label}
                    </p>
                    {loading ? (
                        <Skeleton className="h-7 w-12 mt-2" />
                    ) : (
                        <p className="text-4xl font-bold text-gray-600 dark:text-gray-100 mt-1">
                            {value}
                        </p>
                    )}
                </div>
                <div
                    className={`w-10 h-10 rounded-xl bg-gradient-to-br ${palette[color].bg} flex items-center justify-center text-white shadow-sm`}
                >
                    <Icon className="w-5 h-5" />
                </div>
            </div>
        </button>
    );
};

export default AdminDashboard;
